'use client'

import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../_utils/supabase'
import { motion, useInView } from 'framer-motion'
import { BackgroundBeams } from '@/app/_components/_ui/background-beams'
import CreatorsSection from '../../_components/CreatorsSection'
import { FaUsers, FaStar, FaLightbulb } from 'react-icons/fa'
import GradientButton from '@/app/_components/_ui/gradientButton'
import { useRouter } from 'next/navigation'
import Footer from '@/app/_components/_ui/Footer'

export default function Showcase() {
    const [profiles, setProfiles] = useState([])
    const [loading, setLoading] = useState(true)
    const router = useRouter()

    useEffect(() => {
        const fetchShowcaseProfiles = async () => {
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('username, display_name, tagline, avatar_url')
                    .eq('is_showcase', true)
                    .order('created_at', { ascending: false })

                if (error) throw error
                setProfiles(data || [])
            } catch (error) {
                console.error('Error fetching showcase profiles:', error)
                // Fallback to demo profiles if there's an error
                setProfiles([])
            } finally {
                setLoading(false)
            }
        }

        fetchShowcaseProfiles()
    }, [])

    // Extract usernames from profiles or use fallback defaults
    const showcaseUsernames = profiles.length > 0 
        ? profiles.map(profile => profile.username) 
        : ['nate', 'autofocus', 'echo', 'photography', 'fitness', 'travel'];

    const FeatureSection = () => {
        const ref = useRef(null)
        const isInView = useInView(ref, { once: true, amount: 0.2 })
        
        const features = [
            {
                icon: <FaUsers className="h-6 w-6" />,
                title: "Diverse Creator Community",
                description: "From photographers to fitness enthusiasts, and farmers markets stands to restaurants, our focuses represent a wide range of niches and expertise.",
                colorClass: "bg-purple-500/10 text-purple-400"
            },
            {
                icon: <FaStar className="h-6 w-6" />,
                title: "Exceptional Content Quality",
                description: "These showcase profiles demonstrate the platform's capabilities through beautifully curated content blocks.",
                colorClass: "bg-blue-500/10 text-blue-400"
            },
            {
                icon: <FaLightbulb className="h-6 w-6" />,
                title: "Creative Inspiration",
                description: "Get inspired by how others structure their pages and leverage our flexible content blocks in unique ways.",
                colorClass: "bg-amber-500/10 text-amber-400"
            }
        ]
        
        return (
            <div className="py-16 relative z-10" ref={ref}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.7 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-base text-indigo-400 font-semibold tracking-wide uppercase">
                            Why Browse Our Showcase
                        </h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-white sm:text-4xl">
                            Learn From The Best
                        </p>
                        <p className="mt-4 max-w-2xl text-xl text-gray-300 mx-auto">
                            These featured profiles showcase what's possible with Autofoc.us
                        </p>
                    </motion.div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="bg-neutral-900 p-6 rounded-xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300"
                            >
                                <div className={`flex items-center justify-center h-12 w-12 rounded-md ${feature.colorClass} mb-4`}>
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-medium text-white mb-2">{feature.title}</h3>
                                <p className="text-gray-400">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    const StatsSection = () => {
        const ref = useRef(null)
        const isInView = useInView(ref, { once: true, amount: 0.1 })
        
        const stats = [
            { label: "Showcase Profiles", value: profiles.length || "20+" },
            { label: "Content Blocks", value: "50+" },
            { label: "Unique Visitors", value: "10K+" }
        ]
        
        return (
            <motion.div
                ref={ref}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.7 }}
                className="bg-neutral-900/50 py-12 my-16"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-3 gap-8 text-center">
                        {stats.map((stat, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <p className="text-4xl font-bold text-white">{stat.value}</p>
                                <p className="text-gray-400 mt-2">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>
        )
    }

    const CTASection = () => {
        const ref = useRef(null)
        const isInView = useInView(ref, { once: true, amount: 0.2 })
        
        return (
            <motion.div
                ref={ref}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.7 }}
                className="py-16 text-center"
            >
                <h2 className="text-3xl font-bold text-white mb-6">Want to be featured here?</h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8">
                    These creators have been showcased for their exceptional use of Autofoc.us, demonstrating how we intend the platform to be used. As a thank you, they will often times receive discounted membership. If you believe in our vision and want to share your page with the world, you could be featured here too!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <GradientButton
                        gradient="cyan-blue"
                        text="Create Your Page"
                        className="animate-glow"
                        onClick={() => router.push('/sign-up')}
                    />
                    <GradientButton
                        gradient="purple-pink"
                        text="Learn More"
                        onClick={() => router.push('/about')}
                    />
                </div>
            </motion.div>
        )
    }

    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="container mx-auto relative z-10">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-3xl mx-auto text-center pt-24 pb-8"
                >
                    <h1 className="font-space-grotesk text-5xl font-bold text-white mb-4 uppercase
                                  [text-shadow:_0_0_10px_rgba(255,255,255,0.2),_-2px_-2px_0_#f7d032,_2px_-2px_0_#f76432,_-2px_2px_0_#32f7e1,_2px_2px_0_#7132f7]">
                        Featured Creators
                    </h1>
                    <p className="text-gray-300 text-xl max-w-2xl mx-auto">
                        Explore handpicked profiles that showcase the full potential of Autofoc.us
                    </p>
                </motion.div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                    </div>
                ) : (
                    <>
                        <FeatureSection />
                        
                        <div className="py-16 px-4">
                            <CreatorsSection usernames={showcaseUsernames} />
                        </div>
                        
                        <StatsSection />
                        
                        <CTASection />
                    </>
                )}
            </div>
        </div>
    )
}
